import type { DTechFileConfig } from '../types/DTechFileConfig';

/**
 * Consolidated D-Tech configuration objects
 * 
 * These configs are version-agnostic and work across all D-Tech file versions.
 * Based on analysis of actual fixture files, most D-Tech formats are identical
 * across versions, so we consolidate them to eliminate false ambiguities.
 */
export const DTECH_CONFIGS: Record<string, DTechFileConfig> = {

  /**
   * FData Files - Universal across all versions
   * Structure: Data Page,Date & Time,Elapsed Time,Sample no.,String Speed,Shaft World Angle,Transfer Function,RSTC Version
   */
  DTECH_FDATA: {
    version: 'universal',
    dtechDataType: 'FData',
    timeColumns: ['Date & Time'],
    expectedColumns: [
      'Data Page', 'Date & Time', 'Elapsed Time', 'Sample no.', 
      'String Speed', 'Shaft World Angle', 'Transfer Function', 'RSTC Version'
    ],
    signatureColumns: ['Data Page', 'Sample no.', 'Transfer Function'], // Unique combination for FData
    toolType: 'DTech',
    fileType: 'DTECH_FDATA',
    dataType: 'timeseries',
    timestampFormat: 'YYYY/MM/DD HH:mm:ss',
    eventColumns: ['RSTC Version'],
    signatureThreshold: 3, // All 3 signature columns required for confident detection
    subSecondSampling: true,
    samplesPerPeriod: 40,
    sampleDirection: 'ascending', // F-Data uses 0-39 sample numbering
    sampleIntervalMs: 250 // 40 samples over 10 seconds = 250ms intervals
  },

  /**
   * Turbine Files - Universal across all versions  
   * Structure: Date & Time,Elapsed Time,Sample no.,Turbine Speed,RSTC Version
   */
  DTECH_TURBINE: {
    version: 'universal',
    dtechDataType: 'Turbine',
    timeColumns: ['Date & Time'],
    expectedColumns: ['Date & Time', 'Elapsed Time', 'Sample no.', 'Turbine Speed', 'RSTC Version'],
    signatureColumns: ['Sample no.', 'Turbine Speed'], // Distinctive combination
    toolType: 'DTech',
    fileType: 'DTECH_TURBINE',
    dataType: 'timeseries',
    timestampFormat: 'YYYY/MM/DD HH:mm:ss',
    eventColumns: ['RSTC Version'],
    signatureThreshold: 2, // Both signature columns required
    subSecondSampling: true,
    samplesPerPeriod: 10,
    sampleDirection: 'descending', // Turbine uses 9-0 countdown sample numbering
    sampleIntervalMs: 1000 // 10 samples over 10 seconds = 1000ms intervals
  },

  /**
   * Complete_Log Files - Universal across all versions
   * Structure: Row,Page,Date & Time,Elapsed Time,StartFlag,Inclination,Azimuth,[...100+ columns],RSTC Version
   */
  DTECH_COMPLETE_LOG: {
    version: 'universal',
    dtechDataType: 'Complete_Log',
    timeColumns: ['Date & Time'],
    expectedColumns: [
      'Row', 'Page', 'Date & Time', 'Elapsed Time', 'StartFlag', 
      'Inclination', 'Azimuth', 'Motor PWM', 'RSTC Version'
    ],
    signatureColumns: ['Steering Check Flag', 'Downlink Counter', 'Firmware Version Number'], // Unique to Complete_Log files
    toolType: 'DTech',
    fileType: 'DTECH_COMPLETE_LOG',
    dataType: 'timeseries',
    timestampFormat: 'YYYY/MM/DD HH:mm:ss',
    eventColumns: ['RSTC Version'],
    signatureThreshold: 3 // Strong signature required due to many columns
  },

  /**
   * Downlinks Files - Universal across all versions
   * Structure: Date & Time,Elapsed Time,Row,Steering Table Row,Steering Table Column,Steering Table Mode,Demand Angle,Demand %,Target Inclination,Inclination,Target Azimuth,Azimuth,RSTC Version
   */
  DTECH_DOWNLINKS: {
    version: 'universal',
    dtechDataType: 'Downlinks',
    timeColumns: ['Date & Time'],
    expectedColumns: [
      'Date & Time', 'Elapsed Time', 'Row', 'Steering Table Row', 
      'Steering Table Column', 'Steering Table Mode', 'Demand Angle', 
      'Demand %', 'Target Inclination', 'Inclination', 'Target Azimuth', 
      'Azimuth', 'RSTC Version'
    ],
    signatureColumns: ['Steering Table Row', 'Steering Table Column', 'Steering Table Mode'], // Unique steering table structure
    toolType: 'DTech',
    fileType: 'DTECH_DOWNLINKS',
    dataType: 'timeseries',
    timestampFormat: 'YYYY/MM/DD HH:mm:ss',
    eventColumns: ['RSTC Version'],
    signatureThreshold: 3 // All steering table columns required
  },

  /**
   * Customer_Data Files - Flexible handling for legacy vs modern variants
   * 
   * Legacy (v3_1): 16 columns ending at RSTC Version
   * Modern (V4.0/v3.3): 42 columns with full sensor data
   * 
   * Both share: Row,Page,Date & Time,Elapsed Time,StartFlag,Inclination,Azimuth,Target Inclination,Target Azimuth,String Speed (Avg),Stick Slip Ratio,Turbine,Motor Voltage,Shock,Temperature,RSTC Version
   */
  DTECH_CUSTOMER_DATA: {
    version: 'universal',
    dtechDataType: 'Customer_Data',
    timeColumns: ['Date & Time'],
    expectedColumns: [
      'Row', 'Page', 'Date & Time', 'Elapsed Time', 'StartFlag',
      'Inclination', 'Azimuth', 'Target Inclination', 'Target Azimuth',
      'String Speed (Avg)', 'Stick Slip Ratio', 'Turbine', 'Motor Voltage',
      'Shock', 'Temperature', 'RSTC Version'
      // Note: Modern versions have additional columns after these, but we only require the common set
    ],
    signatureColumns: ['Row', 'Page', 'StartFlag', 'Motor Voltage'], // StartFlag is unique to modern Customer_Data files
    toolType: 'DTech',
    fileType: 'DTECH_CUSTOMER_DATA',
    dataType: 'timeseries',
    timestampFormat: 'YYYY/MM/DD HH:mm:ss',
    eventColumns: ['RSTC Version'],
    signatureThreshold: 3 // Strong signature to distinguish from Complete_Log (which also has Row/Page)
  }

};

/**
 * Get all consolidated D-Tech configs as an array
 */
export function getAllConsolidatedDTechConfigs(): DTechFileConfig[] {
  return Object.values(DTECH_CONFIGS);
}

/**
 * Get a specific config by file type
 */
export function getDTechConfigByType(fileType: string): DTechFileConfig | undefined {
  return DTECH_CONFIGS[fileType];
}

/**
 * Check if a file type is supported by the consolidated configs
 */
export function isSupportedDTechFileType(fileType: string): boolean {
  return fileType in DTECH_CONFIGS;
}